import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileText, CheckCircle2, XCircle, MessageSquareWarning, Pencil, X, Trash2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ImagePreviewDialog } from "@/components/ImagePreviewDialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, fileUrl, openFileBlob } from "@/lib/api";
import { toast } from "sonner";

const EDITABLE_FIELDS = [
  { key: "existing_member_number", label: "No. Ahli Sedia Ada" },
  { key: "full_name", label: "Nama Penuh" },
  { key: "identification_number", label: "No. Kad Pengenalan" },
  { key: "gender", label: "Jantina" },
  { key: "address", label: "Alamat" },
  { key: "division_region", label: "Daerah" },
  { key: "email", label: "Emel" },
  { key: "phone", label: "No. Telefon" },
  { key: "education_level", label: "Tahap Pendidikan" },
  { key: "occupation", label: "Pekerjaan" },
  { key: "course_name", label: "Nama Kursus" },
  { key: "course_date", label: "Tarikh Kursus" },
  { key: "course_session_year", label: "Sesi / Tahun" },
  { key: "course_venue", label: "Tempat Kursus" },
  { key: "next_of_kin_name", label: "Nama Waris" },
  { key: "next_of_kin_relationship", label: "Hubungan Waris" },
  { key: "next_of_kin_phone", label: "No. Telefon Waris" },
];

export default function PermohonanDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permDeleting, setPermDeleting] = useState(false);

  const load = () => api.get(`/applications/${id}`).then((r) => { setApp(r.data); setEditForm(r.data); }).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const canReview = ["super_admin", "secretariat", "president"].includes(user?.role);

  const review = async (action) => {
    setActing(true);
    try {
      const res = await api.post(`/applications/${id}/review`, { action, notes });
      toast.success(
        action === "approve" ? `Ahli diluluskan. No. Ahli: ${res.data.member_number}` :
        action === "reject" ? "Permohonan ditolak" : "Pembetulan diminta daripada pemohon"
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/applications/${id}`, editForm);
      toast.success("Permohonan dikemas kini");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteApplication = async () => {
    setDeleting(true);
    try {
      await api.delete(`/applications/${id}`, { data: { reason: deleteReason || null } });
      toast.success("Permohonan dipindahkan ke rekod dipadam");
      setDeleteDialogOpen(false);
      setDeleteReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const restoreApplication = async () => {
    setRestoring(true);
    try {
      await api.post(`/applications/${id}/restore`);
      toast.success("Permohonan dipulihkan");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRestoring(false);
    }
  };

  const permanentlyDelete = async () => {
    setPermDeleting(true);
    try {
      await api.delete(`/applications/${id}/permanent`);
      toast.success("Permohonan dipadam secara kekal");
      setPermDialogOpen(false);
      window.location.href = "/admin/permohonan";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPermDeleting(false);
    }
  };

  if (!app) return <div className="text-piks-muted">Memuatkan...</div>;

  return (
    <div className="max-w-3xl" data-testid="permohonan-detail-page">
      <Link to="/admin/permohonan" className="inline-flex items-center gap-1.5 text-sm text-piks-muted hover:text-piks-black mb-6"><ArrowLeft className="w-4 h-4" /> Kembali</Link>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="font-display text-xl font-bold text-piks-black">{app.full_name}</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={app.status} />
          {canReview && !editing && !app.deleted_at && (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="permohonan-edit-btn">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDeleteDialogOpen(true)} className="border-piks-red text-piks-red hover:bg-piks-red hover:text-white" data-testid="permohonan-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Padam Permohonan
              </Button>
            </>
          )}
          {canReview && app.deleted_at && (
            <Button size="sm" variant="outline" onClick={restoreApplication} disabled={restoring} data-testid="permohonan-restore-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> {restoring ? "Memulihkan..." : "Pulihkan"}
            </Button>
          )}
          {user?.role === "super_admin" && app.deleted_at && (
            <Button size="sm" variant="outline" onClick={() => setPermDialogOpen(true)} className="border-piks-red text-piks-red hover:bg-piks-red hover:text-white" data-testid="permohonan-permanent-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Padam Kekal
            </Button>
          )}
        </div>
      </div>

      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent data-testid="permohonan-permanent-delete-dialog">
          <DialogHeader>
            <DialogTitle>PADAM KEKAL PERMOHONAN?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-piks-muted">
            Tindakan ini TIDAK BOLEH DIBATALKAN. Permohonan "{app.full_name}" akan dipadam secara kekal dari sistem.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialogOpen(false)} data-testid="permohonan-permanent-delete-cancel-btn">BATAL</Button>
            <Button onClick={permanentlyDelete} disabled={permDeleting} className="bg-piks-red hover:bg-piks-red/90" data-testid="permohonan-permanent-delete-confirm-btn">
              {permDeleting ? "Memadam..." : "PADAM KEKAL"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {app.deleted_at && (
        <div className="bg-piks-red/5 border border-piks-red/30 rounded-xl p-4 mb-6 text-sm" data-testid="permohonan-deleted-banner">
          <p className="font-semibold text-piks-red">Permohonan ini telah dipadam.</p>
          <p className="text-piks-muted mt-1">Dipadam pada {new Date(app.deleted_at).toLocaleString("ms-MY")}{app.delete_reason ? ` — Sebab: ${app.delete_reason}` : ""}</p>
        </div>
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="permohonan-delete-dialog">
          <DialogHeader>
            <DialogTitle>PADAM PERMOHONAN?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-piks-muted">
            Permohonan ini akan dipindahkan ke rekod dipadam dan tidak lagi muncul dalam senarai permohonan aktif.
          </p>
          <div>
            <Label>Sebab Dipadam (pilihan)</Label>
            <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} className="mt-1.5" data-testid="permohonan-delete-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="permohonan-delete-cancel-btn">BATAL</Button>
            <Button onClick={deleteApplication} disabled={deleting} className="bg-piks-red hover:bg-piks-red/90" data-testid="permohonan-delete-confirm-btn">
              {deleting ? "Memadam..." : "PADAM PERMOHONAN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editing ? (
        <div className="bg-white rounded-xl border border-piks-border p-6 mb-6" data-testid="permohonan-edit-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide">Edit Maklumat Permohonan</h2>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditForm(app); }} data-testid="permohonan-edit-cancel-btn"><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {EDITABLE_FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input
                  data-testid={`edit-field-${f.key}`}
                  value={editForm[f.key] || ""}
                  onChange={(e) => setEditForm((cur) => ({ ...cur, [f.key]: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
            ))}
          </div>
          <Button onClick={saveEdit} disabled={savingEdit} data-testid="permohonan-edit-save-btn" className="mt-5 bg-piks-black hover:bg-piks-black/90">
            {savingEdit ? "Menyimpan..." : "Simpan Perubahan"}
          </Button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Maklumat Ahli</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Info label="No. Ahli Sedia Ada" value={app.existing_member_number} />
              <Info label="No. Kad Pengenalan" value={app.identification_number} />
              <Info label="Jantina" value={app.gender} />
              <Info label="Emel" value={app.email} />
              <Info label="No. Telefon" value={app.phone} />
              <Info label="Alamat" value={app.address} />
              <Info label="Daerah" value={app.division_region} />
              <Info label="Tahap Pendidikan" value={app.education_level} />
              <Info label="Pekerjaan" value={app.occupation} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Gambar Pemohon</h2>
            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-piks-muted text-xs mb-2">Gambar Sendiri</p>
                {app.profile_photo_file_id ? (
                  <ImagePreviewDialog testId="detail-profile-photo-link" src={fileUrl(app.profile_photo_file_id)} alt="Gambar Sendiri" className="w-24 h-24 rounded-lg object-cover border border-piks-border" />
                ) : (
                  <p className="text-sm text-piks-muted">Tiada gambar dimuat naik.</p>
                )}
              </div>
              <div>
                <p className="text-piks-muted text-xs mb-2">Gambar Kad Pengenalan (Depan)</p>
                {app.ic_photo_file_id ? (
                  <ImagePreviewDialog testId="detail-ic-photo-link" src={fileUrl(app.ic_photo_file_id)} alt="Gambar IC Depan" className="w-36 h-24 rounded-lg object-cover border border-piks-border" />
                ) : (
                  <p className="text-sm text-piks-muted">Tiada gambar dimuat naik.</p>
                )}
              </div>
              <div>
                <p className="text-piks-muted text-xs mb-2">Gambar Kad Pengenalan (Belakang)</p>
                {app.ic_photo_back_file_id ? (
                  <ImagePreviewDialog testId="detail-ic-photo-back-link" src={fileUrl(app.ic_photo_back_file_id)} alt="Gambar IC Belakang" className="w-36 h-24 rounded-lg object-cover border border-piks-border" />
                ) : (
                  <p className="text-sm text-piks-muted">Tiada gambar dimuat naik.</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Maklumat Kursus</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Info label="Nama Kursus" value={app.course_name} />
              <Info label="Tarikh Kursus" value={app.course_date} />
              <Info label="Sesi / Tahun" value={app.course_session_year} />
              <Info label="Tempat Kursus" value={app.course_venue} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Maklumat Waris</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Info label="Nama Waris" value={app.next_of_kin_name} />
              <Info label="Hubungan" value={app.next_of_kin_relationship} />
              <Info label="No. Telefon Waris" value={app.next_of_kin_phone} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-piks-border p-6 mb-6">
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Dokumen Sokongan</h2>
            {(app.documents || []).length === 0 ? (
              <p className="text-sm text-piks-muted">Tiada dokumen dimuat naik.</p>
            ) : (
              <div className="space-y-2">
                {app.documents.map((d, i) => (
                  <button key={i} type="button" onClick={() => openFileBlob(fileUrl(d.file_id))} className="flex items-center gap-2 text-sm text-piks-black hover:text-piks-red" data-testid={`detail-document-${i}`}>
                    <FileText className="w-4 h-4" /> {d.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {canReview && !editing && !app.deleted_at && ["DIHANTAR", "DALAM_SEMAKAN", "PERLU_MAKLUMAT_TAMBAHAN"].includes(app.status) && (
        <div className="bg-white rounded-xl border border-piks-border p-6">
          <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Tindakan Semakan</h2>
          <Textarea placeholder="Nota semakan (pilihan)" value={notes} onChange={(e) => setNotes(e.target.value)} className="mb-4" data-testid="review-notes-textarea" />
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => review("approve")} disabled={acting} data-testid="review-approve-btn" className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Luluskan
            </Button>
            <Button onClick={() => review("request_info")} disabled={acting} variant="outline" data-testid="review-request-info-btn">
              <MessageSquareWarning className="w-4 h-4 mr-1.5" /> Minta Pembetulan
            </Button>
            <Button onClick={() => review("reject")} disabled={acting} variant="outline" className="border-piks-red text-piks-red hover:bg-piks-red hover:text-white" data-testid="review-reject-btn">
              <XCircle className="w-4 h-4 mr-1.5" /> Tolak
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-piks-muted text-xs">{label}</p>
      <p className="font-medium text-piks-black">{value || "-"}</p>
    </div>
  );
}
